import { useCallback, useEffect, useMemo, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { ButtonSecondary } from '../../buttons/ButtonSecondary'
import { EmptyState } from '../../reusable/emptyState/EmptyState'
import { isOptimisticBatchId, mergeOptimisticRun } from './hooks/useViewThruBatchUpload/viewThruBatchUploadUtils'
import { BatchRun, EnvironmentBadge, StatusBadge, canResumeBatch, count, formatDateTime, formatRunDuration } from './shared'
import styles from './runsTable.module.scss'

export type { BatchRun, BatchRunStatus } from './shared'

const HISTORY_LIMIT = 500
const PAGE_SIZE = 50
const BATCH_HISTORY_URL = 'viewthru-batches'
const RUNNING_POLL_MS = 5_000

interface Failure {
    title: string
    description: string
}

/**
 * A 401 or 404 here means the route isn't served yet rather than anything the
 * user can act on, so say so instead of showing a raw axios message.
 */
const describeFailure = (error: any): Failure => {
    const status = error?.response?.status
    if (status === 401 || status === 404) {
        return {
            title: 'Batch history is not available yet',
            description: `Nothing is serving ${BATCH_HISTORY_URL} on this environment. Batches you run are still saved to S3 and listed in the card above.`,
        }
    }
    return {
        title: 'Could not load previous batches',
        description:
            error?.response?.data?.message || error?.message || 'The request for batch history failed.',
    }
}

interface BatchRunsTableProps {
    reloadToken?: number
    optimisticRun?: BatchRun | null
    onViewDetails: (run: BatchRun) => void
    onResume?: (run: BatchRun) => void
    resumeDisabled?: boolean
}

export const BatchRunsTable = ({
    reloadToken = 0,
    optimisticRun = null,
    onViewDetails,
    onResume,
    resumeDisabled = false,
}: BatchRunsTableProps) => {
    const [runs, setRuns] = useState<BatchRun[]>([])
    const [loading, setLoading] = useState(true)
    const [failure, setFailure] = useState<Failure | null>(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [nowMs, setNowMs] = useState(() => Date.now())

    const loadRuns = useCallback(async () => {
        setLoading(true)
        try {
            const response = await api({ url: BATCH_HISTORY_URL, data: { limit: HISTORY_LIMIT } })
            if (!response.data?.success) throw new Error(response.data?.message || 'Could not load previous batches.')
            setRuns(response.data.batches || [])
            setFailure(null)
        } catch (error: any) {
            setRuns([])
            setFailure(describeFailure(error))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        setCurrentPage(1)
        loadRuns()
    }, [loadRuns, reloadToken])

    const displayedRuns = useMemo(() => mergeOptimisticRun(runs, optimisticRun), [runs, optimisticRun])

    useEffect(() => {
        if (optimisticRun) setCurrentPage(1)
    }, [optimisticRun])

    useEffect(() => {
        const hasRunning = displayedRuns.some((run) => run.status === 'running')
        if (!hasRunning) return
        const timer = setInterval(() => {
            void loadRuns()
        }, RUNNING_POLL_MS)
        return () => clearInterval(timer)
    }, [loadRuns, displayedRuns])

    const hasRunning = useMemo(() => displayedRuns.some((run) => run.status === 'running'), [displayedRuns])

    // Ticks the live Duration cell between the 5s reloads. Display only, no fetch.
    useEffect(() => {
        if (!hasRunning) return
        const timer = setInterval(() => setNowMs(Date.now()), 1_000)
        return () => clearInterval(timer)
    }, [hasRunning])

    const pageCount = Math.max(1, Math.ceil(displayedRuns.length / PAGE_SIZE))
    const page = Math.min(currentPage, pageCount)
    const pageStart = (page - 1) * PAGE_SIZE
    const pageRuns = displayedRuns.slice(pageStart, pageStart + PAGE_SIZE)

    const goToPage = (nextPage: number) => {
        setCurrentPage(Math.min(Math.max(nextPage, 1), pageCount))
    }

    return (
        <div className={styles.section}>
            <div className={`${styles.sectionHeader} ${styles.sectionHeaderPlain}`}>
                <h2>Previous batches</h2>
                <button className={styles.refreshButton} type="button" onClick={loadRuns} disabled={loading}>
                    {loading ? 'Loading...' : 'Refresh'}
                </button>
            </div>

            <div className={styles.tableWrapper}>
                {failure ? (
                    <EmptyState
                        bare
                        tone="error"
                        title={failure.title}
                        description={failure.description}
                        action={<ButtonSecondary onClick={loadRuns}>Try again</ButtonSecondary>}
                    />
                ) : loading && displayedRuns.length === 0 ? (
                    <EmptyState bare tone="loading" title="Loading previous batches" />
                ) : displayedRuns.length === 0 ? (
                    <EmptyState
                        bare
                        title="No batches yet"
                        description="Upload a spreadsheet above and every run will be listed here."
                    />
                ) : (
                    <table className={styles.runsTable}>
                        <thead>
                            <tr>
                                <th>Started</th>
                                <th>Batch</th>
                                <th>Status</th>
                                <th>Results</th>
                                <th className={styles.actionsCell} aria-label="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {pageRuns.map((run) => {
                                const total = count(run.total_count)
                                const complete = count(run.success_count)
                                const duplicates = count(run.duplicate_count)
                                const pending = count(run.pdf_pending_count)
                                const problems =
                                    count(run.blocked_count) +
                                    count(run.challenge_count) +
                                    count(run.error_count) +
                                    count(run.invalid_count)
                                const hasValidCounts =
                                    run.equifax_valid_count != null || run.transunion_valid_count != null
                                const invalidForEquifax = hasValidCounts
                                    ? Math.max(0, total - count(run.equifax_valid_count))
                                    : null
                                const invalidForTransUnion = hasValidCounts
                                    ? Math.max(0, total - count(run.transunion_valid_count))
                                    : null
                                const formatInvalidShare = (invalid: number) => {
                                    if (total <= 0) return `${invalid}`
                                    const pct = Math.round((invalid / total) * 1000) / 10
                                    const pctLabel = Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(1)}%`
                                    return `${invalid} (${pctLabel})`
                                }
                                const optimistic = isOptimisticBatchId(run.id)
                                const openDetails = () => {
                                    if (!optimistic) onViewDetails(run)
                                }
                                const resultMeta = [
                                    run.eligible_count == null ? null : `${count(run.eligible_count)} eligible`,
                                    pending ? `${pending} pending` : null,
                                    problems ? `${problems} problems` : null,
                                ]
                                    .filter(Boolean)
                                    .join(' · ')
                                return (
                                    <tr
                                        key={run.id}
                                        className={optimistic ? undefined : styles.clickableRow}
                                        onClick={openDetails}
                                        onKeyDown={(event) => {
                                            if (event.key !== 'Enter' && event.key !== ' ') return
                                            event.preventDefault()
                                            openDetails()
                                        }}
                                        tabIndex={optimistic ? undefined : 0}
                                    >
                                        <td className={styles.stackedCell}>
                                            <span>{formatDateTime(run.created_at)}</span>
                                            <span className={styles.stackedMeta}>
                                                {formatRunDuration(run, nowMs)}
                                            </span>
                                        </td>
                                        <td className={`${styles.stackedCell} ${styles.fileCell}`}>
                                            <span title={run.filename || undefined}>{run.filename || '—'}</span>
                                            <span className={styles.stackedMeta} title={run.origin || undefined}>
                                                {run.origin || 'No origin'}
                                            </span>
                                        </td>
                                        <td className={styles.stackedCell}>
                                            <StatusBadge status={run.status} />
                                            <span className={styles.stackedBadge}>
                                                <EnvironmentBadge run={run} />
                                            </span>
                                        </td>
                                        <td className={`${styles.stackedCell} ${styles.resultsCell}`}>
                                            <div className={styles.resultsStack}>
                                                <span className={styles.resultsHeadline}>
                                                    {complete} of {total} complete
                                                    {duplicates > 0 ? (
                                                        <span className={styles.resultsHeadlineMeta}>
                                                            · {duplicates} duplicate{duplicates === 1 ? '' : 's'}
                                                        </span>
                                                    ) : null}
                                                </span>
                                                {hasValidCounts &&
                                                invalidForEquifax != null &&
                                                invalidForTransUnion != null ? (
                                                    <span className={styles.resultsMeta}>
                                                        {formatInvalidShare(invalidForEquifax)} invalid for Equifax
                                                        <br />
                                                        {formatInvalidShare(invalidForTransUnion)} invalid for
                                                        TransUnion
                                                    </span>
                                                ) : null}
                                                {resultMeta ? (
                                                    <span className={styles.resultsMeta}>{resultMeta}</span>
                                                ) : null}
                                            </div>
                                        </td>
                                        <td className={styles.actionsCell}>
                                            {onResume && canResumeBatch(run) && (
                                                <ButtonSecondary
                                                    className={styles.detailsButton}
                                                    onClick={(event) => {
                                                        event.stopPropagation()
                                                        onResume(run)
                                                    }}
                                                    disabled={resumeDisabled || optimistic}
                                                >
                                                    Resume
                                                </ButtonSecondary>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {displayedRuns.length > 0 && (
                <div className={styles.paginationBar}>
                    <p className={styles.tableFootnote}>
                        Showing {pageStart + 1}–{pageStart + pageRuns.length} of {displayedRuns.length}
                    </p>
                    {pageCount > 1 && (
                        <div className={styles.paginationControls}>
                            <button
                                className={styles.pageButton}
                                type="button"
                                onClick={() => goToPage(page - 1)}
                                disabled={page === 1}
                            >
                                Previous
                            </button>
                            {Array.from({ length: pageCount }, (_, index) => index + 1)
                                .filter(
                                    (pageNumber) =>
                                        pageNumber === 1 ||
                                        pageNumber === pageCount ||
                                        (pageNumber >= page - 2 && pageNumber <= page + 2)
                                )
                                .map((pageNumber, index, visiblePages) => (
                                    <span key={pageNumber} className={styles.pageNumberGroup}>
                                        {index > 0 && visiblePages[index - 1] !== pageNumber - 1 && (
                                            <span className={styles.pageEllipsis}>…</span>
                                        )}
                                        <button
                                            className={`${styles.pageButton} ${
                                                pageNumber === page ? styles.pageButtonActive : ''
                                            }`}
                                            type="button"
                                            onClick={() => goToPage(pageNumber)}
                                            aria-current={pageNumber === page ? 'page' : undefined}
                                        >
                                            {pageNumber}
                                        </button>
                                    </span>
                                ))}
                            <button
                                className={styles.pageButton}
                                type="button"
                                onClick={() => goToPage(page + 1)}
                                disabled={page === pageCount}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
